import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, ShieldIcon, FingerprintIcon, ScanFaceIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
export type MPINMode = 'enter' | 'set' | 'confirm' | 'success';
export type PinType = 'login' | 'mpin';
interface MPINScreenProps {
  mode: MPINMode;
  pinType?: PinType;
  onComplete: () => void;
  onBack: () => void;
  onModeChange?: (mode: MPINMode) => void;
}
export function MPINScreen({
  mode,
  pinType = 'login',
  onComplete,
  onBack,
  onModeChange
}: MPINScreenProps) {
  const {
    isDark
  } = useTheme();
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState(false);
  const [biometricPhase, setBiometricPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [biometricType, setBiometricType] = useState<'face' | 'thumb'>('thumb');
  const handleBiometricAuth = (type: 'face' | 'thumb') => {
    setBiometricType(type);
    setBiometricPhase('scanning');
    // Simulate scanning
    setTimeout(() => {
      setBiometricPhase('verified');
      // Auto-complete after verified
      setTimeout(() => {
        setBiometricPhase('idle');
        if (mode === 'enter') {
          onComplete();
        }
      }, 800);
    }, 2000);
  };
  const pinLength = pinType === 'mpin' ? 4 : 6;
  // Handle PIN entry
  const handleNumberClick = (num: string) => {
    if (pin.length < pinLength) {
      setPin(prev => prev + num);
      setError(false);
    }
  };
  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError(false);
  };
  // Auto-submit when pin reaches required length
  useEffect(() => {
    if (pin.length === pinLength) {
      const timer = setTimeout(() => {
        if (mode === 'enter') {
          onComplete();
        } else if (mode === 'set') {
          setFirstPin(pin);
          setPin('');
          if (onModeChange) onModeChange('confirm');
        } else if (mode === 'confirm') {
          if (pin === firstPin) {
            if (onModeChange) onModeChange('success');
          } else {
            setError(true);
            setPin('');
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pin, mode, firstPin, onComplete, onModeChange]);
  // Handle success screen timeout
  useEffect(() => {
    if (mode === 'success') {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mode, onComplete]);
  const label = pinType === 'mpin' ? 'MPIN' : 'Login PIN';
  const digits = pinType === 'mpin' ? '4' : '6';
  const getTitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter ${label}`;
      case 'set':
        return `Set ${label}`;
      case 'confirm':
        return `Confirm ${label}`;
      case 'success':
        return 'Success!';
    }
  };
  const getSubtitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter your ${digits}-digit ${label} to continue`;
      case 'set':
        return `Create a secure ${digits}-digit ${label}`;
      case 'confirm':
        return `Re-enter your ${label} to confirm`;
      case 'success':
        return `${label} has been set successfully`;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className={`w-full min-h-screen flex flex-col py-6 px-6 ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]'}`} data-id="element-1448">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1449">
        {mode !== 'success' ? <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1450">
            <ArrowLeftIcon className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} strokeWidth={2} data-id="element-1451" />
          </button> : <div className="w-10" data-id="element-1452" />}
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-1453" />
        <div className="w-10" data-id="element-1454" />
      </div>

      <AnimatePresence mode="wait" data-id="element-1455">
        {mode === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex-1 flex flex-col items-center justify-center -mt-20" data-id="element-1456">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12,
          stiffness: 200
        }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 border-2 border-[#22C55E]/30" data-id="element-1457">
              <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1458" />
            </motion.div>
            <h2 className={`text-[28px] font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1459">
              {getTitle()}
            </h2>
            <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'}`} data-id="element-1460">
              {getSubtitle()}
            </p>
          </motion.div> : <motion.div key="input" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} className="flex-1 flex flex-col" data-id="element-1461">
            {/* Title */}
            <div className="mb-12 text-center" data-id="element-1462">
              <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FCECE3]'}`} data-id="element-1463">
                <ShieldIcon className="w-6 h-6 text-[#D95A1A]" strokeWidth={2} data-id="element-1464" />
              </div>
              <h1 className={`text-[32px] font-bold leading-tight tracking-tight mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1465">
                {getTitle()}
              </h1>
              <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'}`} data-id="element-1466">
                {getSubtitle()}
              </p>
            </div>

            {/* PIN Display */}
            <div className="flex justify-center gap-3 mb-12" data-id="element-1467">
              {[...Array(pinLength)].map((_, i) => <motion.div key={i} animate={error ? {
            x: [-5, 5, -5, 5, 0]
          } : {
            scale: pin[i] ? [1, 1.1, 1] : 1
          }} transition={{
            duration: 0.2
          }} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${error ? 'bg-red-50 text-red-500 border-2 border-red-500' : pin[i] ? isDark ? 'bg-[#3D2518] text-white border-2 border-[#D95A1A]' : 'bg-white text-[#1A1A1A] border-2 border-[#D95A1A] shadow-sm' : isDark ? 'bg-[#2D1810] border-2 border-transparent' : 'bg-[#FCECE3] border-2 border-transparent'}`} data-id="element-1468">
                  {pin[i] ? '•' : ''}
                </motion.div>)}
            </div>

            {error && <p className="text-red-500 text-center text-sm font-medium -mt-6 mb-6" data-id="element-1469">
                PINs do not match. Please try again.
              </p>}

            {/* Keypad */}
            <div className="mt-auto grid grid-cols-3 gap-y-6 gap-x-4 max-w-[300px] mx-auto w-full pb-8" data-id="element-1470">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => handleNumberClick(num.toString())} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#1A1A1A] hover:bg-white'}`} data-id="element-1471">
                  {num}
                </button>)}
              <div className="flex items-center justify-center" data-id="element-1472">
                {mode === 'enter' && pinType === 'login' && <button onClick={() => handleBiometricAuth('thumb')} className={`h-14 w-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#F37024] hover:bg-[#2D1810]' : 'text-[#D95A1A] hover:bg-white'}`} data-id="element-1473">
                    <FingerprintIcon className="w-8 h-8" strokeWidth={1.5} data-id="element-1474" />
                  </button>}
              </div>
              <button onClick={() => handleNumberClick('0')} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#1A1A1A] hover:bg-white'}`} data-id="element-1475">
                0
              </button>
              <button onClick={handleDelete} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#B8A89E] hover:bg-[#2D1810]' : 'text-[#4A3B32] hover:bg-white'}`} data-id="element-1476">
                <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-1477" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Biometric Overlay */}
      <AnimatePresence data-id="element-1478">
        {biometricPhase !== 'idle' && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
        background: isDark ? 'linear-gradient(180deg, rgba(26,14,8,0.95) 0%, rgba(45,24,16,0.95) 100%)' : 'linear-gradient(180deg, rgba(255,247,243,0.95) 0%, rgba(253,238,229,0.95) 100%)',
        backdropFilter: 'blur(10px)'
      }} data-id="element-1479">
            <AnimatePresence mode="wait" data-id="element-1480">
              {biometricPhase === 'scanning' && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center gap-6" data-id="element-1481">
                  <motion.div animate={{
              scale: [1, 1.15, 1],
              opacity: [0.7, 1, 0.7]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
              background: 'rgba(217, 90, 26, 0.12)',
              border: '2px solid rgba(217, 90, 26, 0.3)'
            }} data-id="element-1482">
                    {biometricType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#D95A1A]" strokeWidth={1.5} data-id="element-1483" /> : <ScanFaceIcon className="w-12 h-12 text-[#D95A1A]" strokeWidth={1.5} data-id="element-1484" />}
                  </motion.div>
                  <div className="text-center" data-id="element-1485">
                    <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1486">
                      {biometricType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                    </p>
                    <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'}`} data-id="element-1487">
                      {biometricType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                    </p>
                  </div>
                  <button onClick={() => setBiometricPhase('idle')} className={`mt-4 text-[14px] font-bold underline ${isDark ? 'text-[#F37024]' : 'text-[#D95A1A]'}`} data-id="element-1488">
                    Cancel
                  </button>
                </motion.div>}

              {biometricPhase === 'verified' && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center gap-6" data-id="element-1489">
                  <motion.div initial={{
              scale: 0.5
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 12,
              stiffness: 200
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
              border: '2px solid rgba(34, 197, 94, 0.3)'
            }} data-id="element-1490">
                    <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1491" />
                  </motion.div>
                  <div className="text-center" data-id="element-1492">
                    <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1493">
                      Verified Successfully
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}